import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Shield, Award, CheckCircle, Star, Menu, X, ChevronRight, ChevronDown, ChevronUp, Sparkles, ArrowRight, Trash2, Edit, Eye, LogOut, User as UserIcon, Lock } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./pages/AuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== FLOATING COMPONENTS ==============

const FloatingWhatsApp = () => (
  <a
    href="https://wa.me/447300405045?text=Hi%2C%20I%27m%20interested%20in%20getting%20a%20quote%20from%20Expert-Painter."
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 flex items-center gap-2 bg-green-500 hover:bg-green-600 rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all hover:scale-105"
    aria-label="Chat on WhatsApp"
    data-testid="whatsapp-btn"
  >
    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
    <span className="text-white font-semibold text-sm hidden sm:inline">WhatsApp</span>
  </a>
);

const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty deps - handleScroll is defined inside effect

  // Don't show on admin page
  if (location.pathname.startsWith('/admin')) return null;
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 md:hidden z-50 shadow-lg">
      <div className="flex gap-2">
        <a
          href="tel:07300405045"
          className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
          data-testid="floating-call-btn"
        >
          <Phone className="w-5 h-5" />
          Call Now
        </a>
        <Link
          to="/contact"
          className="flex-1 bg-sky-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
          data-testid="floating-quote-btn"
        >
          Free Quote
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

// ============== NAVIGATION ==============

const Navigation = ({ user, login, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/interior", label: "Interior" },
    { path: "/exterior", label: "Exterior" },
    { path: "/wallpapering", label: "Wallpapering" },
    { path: "/contact", label: "Get Quote" },
  ];

  // Don't show nav on admin page
  if (location.pathname.startsWith('/admin')) return null;

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center py-2">
          <Link to="/" className="flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_craftypaint/artifacts/fnc69mdr_expert-painter%20logo%20new.png" 
              alt="Expert Painter" 
              className="h-12 sm:h-14 md:h-16 w-auto"
            />
          </Link>
          
          {/* Desktop Nav - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1 px-8">
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors ${location.pathname === link.path ? 'text-sky-600' : 'text-gray-700 hover:text-sky-600'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Desktop Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/my-enquiries" className="flex items-center gap-2 text-gray-600 hover:text-sky-600">
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  <span className="text-sm font-medium">My Enquiries</span>
                </Link>
                <button onClick={logout} className="text-gray-600 hover:text-gray-900">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={login}
                className="text-gray-600 hover:text-gray-900 flex items-center gap-1 text-sm"
              >
                <UserIcon className="w-4 h-4" />
                Sign in
              </button>
            )}
            <a
              href="tel:07300405045"
              className="bg-gray-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              07300 405045
            </a>
          </div>

          {/* Mobile: Phone + Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href="tel:07300405045"
              className="bg-sky-600 text-white p-2.5 rounded-lg"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2.5 text-gray-600 bg-gray-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t animate-fadeIn">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block py-3 px-2 font-medium rounded-lg ${location.pathname === link.path ? 'text-sky-600 bg-sky-50' : 'text-gray-700'}`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  to="/my-enquiries"
                  onClick={() => setIsOpen(false)}
                  className={`block py-3 px-2 font-medium rounded-lg border-t mt-2 ${location.pathname === '/my-enquiries' ? 'text-sky-600 bg-sky-50' : 'text-gray-700'}`}
                >
                  My Enquiries
                </Link>
                <div className="flex items-center justify-between py-3 px-2 border-t mt-2">
                  <div className="flex items-center gap-2">
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    <span className="text-sm text-gray-700">{user.name}</span>
                  </div>
                  <button onClick={logout} className="text-gray-600">
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={login}
                className="w-full py-3 px-2 text-left font-medium text-gray-700 border-t mt-2 flex items-center gap-2"
              >
                <UserIcon className="w-5 h-5" />
                Sign in with Google
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// ============== TRUST BADGES ==============

const TrustBadges = ({ variant = "light" }) => {
  const badges = [
    { icon: Shield, text: "12-Month Guarantee" },
    { icon: Award, text: "Beat Any Quote" },
    { icon: CheckCircle, text: "No VAT" },
    { icon: Star, text: "Fully Insured" },
  ];

  const bgClass = variant === "dark" ? "bg-gray-800/50" : "bg-white/10";
  const textClass = variant === "dark" ? "text-white" : "text-white";

  return (
    <div className={`flex flex-wrap justify-center gap-2 sm:gap-4 ${bgClass} rounded-xl p-3`}>
      {badges.map((badge) => (
        <div key={badge.text} className={`flex items-center gap-1.5 text-xs sm:text-sm ${textClass}`}>
          <badge.icon className="w-4 h-4 text-sky-400" />
          <span>{badge.text}</span>
        </div>
      ))}
    </div>
  );
};

// ============== CONTACT FORM ==============

const ContactForm = ({ preSelectedService = "", user = null }) => {
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    service_type: preSelectedService || "",
    postcode: "",
    address: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submitData = { ...formData };
      if (user?.user_id) {
        submitData.user_id = user.user_id;
      }
      const response = await axios.post(`${API}/contact`, submitData);
      if (response.data.success) {
        setSubmitStatus({ type: "success", message: response.data.message });
        setFormData({ name: "", email: "", phone: "", service_type: "", postcode: "", address: "", message: "" });
      }
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Something went wrong. Please try again or call us." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === "success") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">We&apos;ve received your enquiry and will be in touch within 24 hours.</p>
        <p className="text-sm text-gray-500">Need us sooner? Call <a href="tel:07300405045" className="text-sky-600 font-semibold">07300 405045</a></p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
            placeholder="Your name"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
            placeholder="07xxx xxxxxx"
            data-testid="contact-phone-input"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
            placeholder="your@email.com"
            data-testid="contact-email-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
          <input
            type="text"
            name="postcode"
            value={formData.postcode}
            onChange={handleChange}
            className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
            placeholder="M1 1AA"
            data-testid="contact-postcode-input"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
          placeholder="Your address"
          data-testid="contact-address-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Required *</label>
        <select
          name="service_type"
          value={formData.service_type}
          onChange={handleChange}
          required
          className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base bg-white"
          data-testid="contact-service-select"
        >
          <option value="">Select a service...</option>
          <option value="interior">Interior Painting</option>
          <option value="exterior">Exterior Painting</option>
          <option value="wallpapering">Wallpapering & Decorating</option>
          <option value="commercial">Commercial Painting</option>
          <option value="landlord">Landlord Services</option>
          <option value="full_house">Full House Redecoration</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your project</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent text-base"
          placeholder="What would you like painted?"
          data-testid="contact-message-input"
        />
      </div>
      
      {submitStatus?.type === "error" && (
        <div className="p-4 rounded-xl bg-red-50 text-red-800" data-testid="form-error">
          {submitStatus.message}
        </div>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-sky-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-sky-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-sky-600/30"
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? "Sending..." : "Get My Free Quote"}
        {!isSubmitting && <ArrowRight className="w-5 h-5" />}
      </button>
    </form>
  );
};

// ============== FOOTER ==============

const Footer = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;

  return (
    <footer className="bg-gray-900 text-gray-300 pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="sm:col-span-2 lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_craftypaint/artifacts/fnc69mdr_expert-painter%20logo%20new.png" 
              alt="Expert Painter" 
              className="h-12 w-auto mb-4"
            />
            <p className="text-sm">Professional painting and decorating in Greater Manchester.</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Services</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/interior" className="hover:text-sky-400">Interior Painting</Link></li>
              <li><Link to="/exterior" className="hover:text-sky-400">Exterior Painting</Link></li>
              <li><Link to="/wallpapering" className="hover:text-sky-400">Wallpapering</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Contact</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="tel:07300405045" className="flex items-center gap-2 hover:text-sky-400">
                  <Phone className="w-4 h-4" /> 07300 405045
                </a>
              </li>
              <li>
                <a href="mailto:enquiries@expert-painter.co.uk" className="flex items-center gap-2 hover:text-sky-400">
                  <Mail className="w-4 h-4" /> enquiries@expert-painter.co.uk
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Greater Manchester
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Our Promise</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-sky-400" /> 12-Month Guarantee</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-sky-400" /> Beat Any Quote</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-sky-400" /> No VAT</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm">
          <p>© {new Date().getFullYear()} Expert Painter. All rights reserved.</p>
          <Link to="/admin" className="text-gray-600 hover:text-gray-400 text-xs mt-2 inline-block">Admin</Link>
        </div>
      </div>
    </footer>
  );
};

// ============== ADMIN PANEL ==============

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/verify`, { password });
      if (response.data.valid) {
        sessionStorage.setItem("admin_key", password);
        onLogin(password);
      } else {
        setError("Invalid password");
      }
    } catch (err) {
      setError("Failed to verify. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-sky-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Enter admin password to access dashboard</p>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-sky-600 text-white py-3 rounded-xl font-semibold hover:bg-sky-700 disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {isLoading ? "Verifying..." : "Login"}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sky-600 hover:underline text-sm">← Back to website</Link>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = ({ adminKey, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedQuote, setSelectedQuote] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [quotesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes?admin_key=${adminKey}${filter !== 'all' ? `&status=${filter}` : ''}`),
        axios.get(`${API}/admin/stats?admin_key=${adminKey}`)
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      // Error fetching data - silent fail for admin dashboard
    } finally {
      setLoading(false);
    }
  }, [adminKey, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}?admin_key=${adminKey}`, { status: newStatus });
      fetchData();
      setSelectedQuote(null);
    } catch (error) {
      // Error updating status - silent fail
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}?admin_key=${adminKey}`);
      fetchData();
      setSelectedQuote(null);
      setDeleteConfirm(null);
    } catch (error) {
      alert("Failed to delete lead. Please try again.");
    }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-800",
    contacted: "bg-yellow-100 text-yellow-800",
    quoted: "bg-purple-100 text-purple-800",
    booked: "bg-green-100 text-green-800",
    completed: "bg-gray-100 text-gray-800",
    closed: "bg-red-100 text-red-800"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Expert Painter Admin</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sky-600 hover:underline text-sm">View Website</Link>
            <button onClick={onLogout} className="text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            {[
              { label: "Total", value: stats.total, color: "bg-gray-900" },
              { label: "New", value: stats.new, color: "bg-blue-600" },
              { label: "Contacted", value: stats.contacted, color: "bg-yellow-600" },
              { label: "Quoted", value: stats.quoted, color: "bg-purple-600" },
              { label: "Booked", value: stats.booked, color: "bg-green-600" },
              { label: "Completed", value: stats.completed, color: "bg-gray-600" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color.replace('bg-', 'text-')}`}>{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-2">
            {["all", "new", "contacted", "quoted", "booked", "completed", "closed"].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Quotes Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{quote.name}</p>
                        <p className="text-sm text-gray-500">{quote.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{quote.service_name}</td>
                    <td className="px-4 py-4">
                      <a href={`tel:${quote.phone}`} className="text-sky-600 hover:underline text-sm">{quote.phone}</a>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[quote.status] || 'bg-gray-100'}`}>
                        {quote.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedQuote(quote)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(quote)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                          data-testid={`delete-btn-${quote.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {quotes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No leads found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-bold text-gray-900">{selectedQuote.name}</h2>
                <button onClick={() => setSelectedQuote(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a href={`tel:${selectedQuote.phone}`} className="text-sky-600 font-medium">{selectedQuote.phone}</a>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a href={`mailto:${selectedQuote.email}`} className="text-sky-600 font-medium text-sm break-all">{selectedQuote.email}</a>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Service</p>
                  <p className="font-medium">{selectedQuote.service_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Postcode</p>
                  <p className="font-medium">{selectedQuote.postcode || 'N/A'}</p>
                </div>
              </div>
              {selectedQuote.address && (
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">{selectedQuote.address}</p>
                </div>
              )}
              {selectedQuote.message && (
                <div>
                  <p className="text-sm text-gray-500">Message</p>
                  <p className="bg-gray-50 p-3 rounded-lg text-sm">{selectedQuote.message}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-2">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "quoted", "booked", "completed", "closed"].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateStatus(selectedQuote.id, status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        selectedQuote.status === status 
                          ? statusColors[status] 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t flex gap-2">
                <a
                  href={`tel:${selectedQuote.phone}`}
                  className="flex-1 bg-sky-600 text-white py-2 rounded-lg font-medium text-center"
                >
                  Call
                </a>
                <a
                  href={`mailto:${selectedQuote.email}`}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium text-center"
                >
                  Email
                </a>
                <a
                  href={`https://wa.me/${selectedQuote.phone.replace(/\s/g, '').replace(/^0/, '44')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-green-500 text-white py-2 rounded-lg font-medium text-center"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Lead?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteQuote(deleteConfirm.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem("admin_key") || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const verifyStoredKey = async () => {
      const storedKey = sessionStorage.getItem("admin_key");
      if (storedKey) {
        try {
          const response = await axios.post(`${API}/admin/verify`, { password: storedKey });
          if (response.data.valid) {
            setAdminKey(storedKey);
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem("admin_key");
            setAdminKey("");
          }
        } catch (err) {
          sessionStorage.removeItem("admin_key");
          setAdminKey("");
        }
      }
      setIsChecking(false);
    };
    verifyStoredKey();
  }, []); // Empty deps - runs once on mount

  const handleLogin = (key) => {
    setAdminKey(key);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_key");
    setAdminKey("");
    setIsAuthenticated(false);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard adminKey={adminKey} onLogout={handleLogout} />;
};

// ============== PAGES (Shortened for brevity - keeping key components) ==============

const HomePage = () => {
  const services = [
    { title: "Interior Painting", description: "Professional interior painter for walls, ceilings, woodwork.", image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=300&fit=crop", alt: "Interior painting service", link: "/interior" },
    { title: "Exterior Painting", description: "Expert exterior painter for house fronts, fascias, fences.", image: "https://images.unsplash.com/photo-1613844044163-1ad2f2d0b152?w=400&h=300&fit=crop", alt: "Exterior painting service", link: "/exterior" },
    { title: "Wallpapering", description: "Professional wallpaper hanging and decorator services.", image: "https://images.unsplash.com/photo-1672130515395-32583062f72b?w=400&h=300&fit=crop", alt: "Wallpapering service", link: "/wallpapering" }
  ];

  const testimonials = [
    { quote: "Had the whole house painted. Fantastic finish, very tidy, and half the price I was quoted elsewhere!", name: "Sarah M.", location: "Stockport" },
    { quote: "House exterior done before selling. Quick turnaround and we got over asking price.", name: "Dave T.", location: "Cheadle" },
    { quote: "Wallpapered our feature wall perfectly. No bubbles, perfect alignment.", name: "Karen B.", location: "Bramhall" }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/6474496/pexels-photo-6474496.jpeg?auto=compress&cs=tinysrgb&w=1200')] bg-cover bg-center opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:py-20 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-sky-600/20 text-sky-400 px-4 py-2 rounded-full text-sm mb-6">
              <Sparkles className="w-4 h-4" /> Free quotes • No obligation
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight">
              Professional Painter &amp; Decorator
              <span className="block text-sky-400 mt-2">in Greater Manchester</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Expert painting and decorating services. Save up to 30% vs big agencies. No VAT charged.
            </p>
            <TrustBadges />
            <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
              <Link to="/contact" className="bg-sky-600 text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-sky-700 transition-all shadow-lg text-lg">
                Get a Free Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="tel:07300405045" className="bg-white text-gray-900 px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-100 transition-all text-lg">
                <Phone className="w-5 h-5" /> 07300 405045
              </a>
            </div>
            <p className="text-gray-400 text-sm mt-6"><Clock className="w-4 h-4 inline mr-1" /> We respond within 2 hours</p>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
            <span className="ml-1 font-medium">5.0 Rating</span>
          </div>
          <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span>100+ Happy Customers</span></div>
          <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-sky-500" /><span>Fully Insured</span></div>
        </div>
      </section>

      {/* Trades Banner */}
      <section className="bg-white py-6 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <img src="https://customer-assets.emergentagent.com/job_craftypaint/artifacts/u8j14tyx_Trades%20Logo.png" alt="Featured on trade platforms" className="w-full h-auto" />
        </div>
      </section>

      {/* Services */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Painting &amp; Decorating Services</h2>
            <p className="text-gray-600">Professional painter and decorator for every project</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link key={service.link} to={service.link} className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all overflow-hidden border border-gray-100">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={service.image} alt={service.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-sky-600">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{service.description}</p>
                  <span className="text-sky-600 font-medium text-sm flex items-center gap-1">Learn more <ChevronRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Why Choose Expert Painter?</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Shield, title: "12-Month Guarantee", desc: "All painting work guaranteed" },
              { icon: Award, title: "Beat Any Quote", desc: "By 10% guaranteed" },
              { icon: CheckCircle, title: "No VAT", desc: "Price you see is what you pay" },
              { icon: Clock, title: "Quick Response", desc: "Quotes within 24 hours" },
            ].map((item) => (
              <div key={item.title} className="bg-white p-4 sm:p-6 rounded-xl text-center shadow-sm">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1">{item.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">What Our Customers Say</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-gray-50 p-5 sm:p-6 rounded-xl">
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-700 text-sm sm:text-base mb-4">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                    <span className="text-sky-600 font-bold text-sm">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Areas We Cover</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {['Manchester', 'Stockport', 'Cheadle', 'Bramhall', 'Sale', 'Altrincham', 'Didsbury', 'Chorlton', 'Wilmslow', 'Heald Green'].map((area) => (
              <span key={area} className="bg-white px-4 py-2 rounded-full text-sm text-gray-700 shadow-sm border border-gray-100">
                Painter in {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-sky-600 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to Transform Your Space?</h2>
          <p className="text-sky-100 mb-6">Get a free, no-obligation quote today</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="bg-white text-sky-600 px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-100">
              Get Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:07300405045" className="bg-sky-700 text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-sky-800">
              <Phone className="w-5 h-5" /> 07300 405045
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServicePage = ({ service }) => {
  const serviceData = {
    interior: { title: "Interior Painting & Decorating", subtitle: "Professional interior painter in Greater Manchester", description: "Expert interior painting for homes across Manchester.", image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&h=500&fit=crop", features: ["Living rooms & bedrooms", "Kitchens & bathrooms", "Woodwork & trim", "Ceiling painting"] },
    exterior: { title: "Exterior Painting & Decorating", subtitle: "Professional exterior painter in Greater Manchester", description: "Weather-resistant exterior painting for homes.", image: "https://images.unsplash.com/photo-1613844044163-1ad2f2d0b152?w=800&h=500&fit=crop", features: ["House exteriors", "Fascias & soffits", "Fences & gates", "Render painting"] },
    wallpapering: { title: "Wallpapering & Decorating", subtitle: "Professional wallpaper hanging in Greater Manchester", description: "Expert wallpaper hanging and removal services.", image: "https://images.unsplash.com/photo-1672130515395-32583062f72b?w=800&h=500&fit=crop", features: ["Wallpaper hanging", "Paper removal", "Feature walls", "Lining paper"] }
  };
  const data = serviceData[service];

  return (
    <div>
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12 sm:py-16">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${data.image})` }}></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">{data.title}</h1>
            <p className="text-lg text-sky-400 mb-4">{data.subtitle}</p>
            <p className="text-gray-300 mb-6">{data.description}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="bg-sky-600 text-white px-6 py-3.5 rounded-xl font-bold flex items-center justify-center gap-2">Get Free Quote <ArrowRight className="w-5 h-5" /></Link>
              <a href="tel:07300405045" className="bg-white/10 text-white px-6 py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 border border-white/20"><Phone className="w-5 h-5" /> 07300 405045</a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What&apos;s Included</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.features.map((feature) => (
              <div key={feature} className="bg-gray-50 p-4 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />
                <span className="text-gray-800 text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-12 bg-sky-600 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-3">Get Your Free Quote</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" state={{ service }} className="bg-white text-sky-600 px-8 py-4 rounded-xl font-bold">Get Free Quote</Link>
            <a href="tel:07300405045" className="bg-sky-700 text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-2"><Phone className="w-5 h-5" /> Call Now</a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactPage = ({ user }) => {
  const location = useLocation();
  const preSelectedService = location.state?.service || "";
  const [openFaq, setOpenFaq] = useState(null);
  const faqs = [
    { q: "How does the quote process work?", a: "Fill in the form or call us. We'll arrange a time to visit and provide a detailed quote." },
    { q: "Do I need to provide the paint?", a: "No - we supply all paints and materials using quality trade paints." },
    { q: "What does the guarantee cover?", a: "Our 12-month guarantee covers any defects in workmanship." },
    { q: "What areas do you cover?", a: "Greater Manchester including Stockport, Cheadle, Bramhall, Sale, Altrincham, and more." }
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Get Your Free Quote</h1>
          <p className="text-gray-300">Usually respond within 2 hours</p>
        </div>
      </section>
      <section className="py-10 sm:py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Tell us about your project</h2>
              <ContactForm preSelectedService={preSelectedService} user={user} />
            </div>
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <h3 className="font-bold text-gray-900 mb-4">Prefer to talk?</h3>
                <a href="tel:07300405045" className="flex items-center gap-3 text-xl font-bold text-sky-600 hover:text-sky-700 mb-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center"><Phone className="w-6 h-6 text-sky-600" /></div>
                  07300 405045
                </a>
                <div className="space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> enquiries@expert-painter.co.uk</p>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Greater Manchester</p>
                  <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> Mon-Sat: 8am - 6pm</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[{ icon: Shield, text: "12-Month Guarantee" }, { icon: Star, text: "5-Star Reviews" }, { icon: CheckCircle, text: "No VAT" }, { icon: Award, text: "Fully Insured" }].map((item) => (
                  <div key={item.text} className="bg-white p-4 rounded-xl shadow-sm text-center">
                    <item.icon className="w-6 h-6 text-sky-600 mx-auto mb-1" />
                    <p className="text-xs font-medium text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Answers</h2>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-gray-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === faq.q ? null : faq.q)} className="w-full flex items-center justify-between p-4 text-left font-medium text-gray-900 hover:bg-gray-50">
                  {faq.q}
                  {openFaq === faq.q ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {openFaq === faq.q && <div className="px-4 pb-4 text-gray-600 text-sm">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ============== MY ENQUIRIES PAGE ==============

const MyEnquiriesPage = ({ user, login }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEnquiries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/my-quotes`, { withCredentials: true });
      setEnquiries(response.data);
    } catch (error) {
      // Error fetching enquiries - silent fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchEnquiries();
  }, [user, fetchEnquiries]);

  const statusColors = {
    new: { bg: "bg-blue-100", text: "text-blue-800", label: "Submitted" },
    contacted: { bg: "bg-yellow-100", text: "text-yellow-800", label: "We've been in touch" },
    quoted: { bg: "bg-purple-100", text: "text-purple-800", label: "Quote sent" },
    booked: { bg: "bg-green-100", text: "text-green-800", label: "Job booked" },
    completed: { bg: "bg-gray-100", text: "text-gray-800", label: "Completed" },
    closed: { bg: "bg-red-100", text: "text-red-800", label: "Closed" }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">My Enquiries</h1>
            <p className="text-gray-300">Sign in to view and track your enquiries</p>
          </div>
        </section>
        <section className="py-16">
          <div className="max-w-md mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-sky-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to continue</h2>
              <p className="text-gray-600 mb-6">Sign in with Google to view your enquiries and track their status.</p>
              <button
                onClick={login}
                className="w-full bg-sky-600 text-white py-3 rounded-xl font-semibold hover:bg-sky-700 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-white/20" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">My Enquiries</h1>
              <p className="text-gray-300">{user.email}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4">
          {enquiries.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">No enquiries yet</h2>
              <p className="text-gray-600 mb-6">Submit your first enquiry to get a free quote!</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-sky-700"
              >
                Get a Free Quote <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {enquiries.map((enquiry) => {
                const status = statusColors[enquiry.status] || statusColors.new;
                return (
                  <div key={enquiry.id} className="bg-white rounded-xl shadow-sm p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-gray-900">{enquiry.service_name}</h3>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                            {status.label}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{enquiry.message || "No message provided"}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          {enquiry.postcode && <span>📍 {enquiry.postcode}</span>}
                          <span>📅 {new Date(enquiry.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      </div>
                      <div className="flex sm:flex-col gap-2">
                        <a
                          href="tel:07300405045"
                          className="flex-1 sm:flex-none bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                        >
                          <Phone className="w-4 h-4" /> Call us
                        </a>
                      </div>
                    </div>
                    
                    {/* Status Timeline */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs">
                        {['new', 'contacted', 'quoted', 'booked', 'completed'].map((step, index) => {
                          const stepOrder = ['new', 'contacted', 'quoted', 'booked', 'completed'];
                          const currentIndex = stepOrder.indexOf(enquiry.status);
                          const isComplete = index <= currentIndex;
                          const isCurrent = index === currentIndex;
                          
                          return (
                            <div key={step} className="flex flex-col items-center flex-1">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                                isComplete ? 'bg-sky-600 text-white' : 'bg-gray-200 text-gray-400'
                              } ${isCurrent ? 'ring-2 ring-sky-300' : ''}`}>
                                {isComplete ? <CheckCircle className="w-4 h-4" /> : <span className="text-xs">{index + 1}</span>}
                              </div>
                              <span className={`text-center ${isComplete ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                                {step === 'new' ? 'Sent' : step.charAt(0).toUpperCase() + step.slice(1)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <div className="text-center pt-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sky-600 font-medium hover:text-sky-700"
                >
                  Submit another enquiry <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ============== APP ROUTER ==============

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();

  // Check for session_id in URL hash - MUST be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Navigation user={user} login={login} logout={logout} />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/interior" element={<ServicePage service="interior" />} />
          <Route path="/exterior" element={<ServicePage service="exterior" />} />
          <Route path="/wallpapering" element={<ServicePage service="wallpapering" />} />
          <Route path="/contact" element={<ContactPage user={user} />} />
          <Route path="/my-enquiries" element={<MyEnquiriesPage user={user} login={login} />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </main>
      <Footer />
      <FloatingWhatsApp />
      <FloatingCTA />
    </>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
